import React, { useMemo, useState } from 'react';
import { DataTable } from '@components/organisms/Documents/components/DataTable';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { SourceBadge } from '@components/atoms/SourceBadge';
import { MaterialSource } from '@models/warehouse.model';

type InventoryItem = {
  totalPrice?: number | null;
  unitPrice?: number | null;
  totalCost?: number | null;
  unitCost?: number | null;
  isOwnItem?: boolean;
  isSubcontracted?: boolean;
  originalOrganizationName?: string;
  chargeToClient?: boolean;
  amount?: number;
  name?: string;
  equipmentId?: string;
  time?: string;
  taskName?: string;
  unassigned?: boolean;
  displayOrganizationName?: string;
  costPerHour?: number | null;
  source?: 'WAREHOUSE' | 'MANUAL';
};

interface MaterialsEquipmentTabProps {
  inventory: InventoryItem[];
  isDelegated: boolean;
}

const formatCurrency = (value?: number | null) => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return `$ ${value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  return new Date(dateString).toLocaleString('en-US', options);
};

export const MaterialsEquipmentTab: React.FC<MaterialsEquipmentTabProps> = ({
  inventory,
  isDelegated,
}) => {
  const [filterMode, setFilterMode] = useState<
    'all' | 'subcontracted' | 'not_subcontracted' | 'previous_contractor'
  >('all');

  const filterOptions: SelectOption[] = useMemo(
    () => [
      { value: 'all', label: 'All Items' },
      { value: 'subcontracted', label: 'Subcontracted' },
      { value: 'not_subcontracted', label: 'Not Subcontracted' },
      { value: 'previous_contractor', label: 'Previous contractor' },
    ],
    [],
  );

  const selectedFilter = useMemo<SelectOption>(() => {
    switch (filterMode) {
      case 'subcontracted':
        return { value: 'subcontracted', label: 'Subcontracted' };
      case 'not_subcontracted':
        return { value: 'not_subcontracted', label: 'Not Subcontracted' };
      case 'previous_contractor':
        return { value: 'previous_contractor', label: 'Previous contractor' };
      default:
        return { value: 'all', label: 'All Items' };
    }
  }, [filterMode]);

  const { filteredInventory, showPriceColumns, totalCostToDate, totalSalesToDate, rawProfit } =
    useMemo(() => {
      const isItemSubcontracted = (item: InventoryItem) => {
        if (typeof item.isOwnItem === 'boolean') return !item.isOwnItem;
        if (typeof item.isSubcontracted === 'boolean')
          return item.isSubcontracted;
        return false;
      };

      const filtered = inventory
        .filter((item) => {
          if (filterMode === 'subcontracted') return isItemSubcontracted(item);
          if (filterMode === 'not_subcontracted')
            return !isItemSubcontracted(item);
          if (filterMode === 'previous_contractor')
            return item.unassigned && !item.isOwnItem;
          return true;
        })
        .filter((item) => {
          const hasValidTotalPrice =
            item.totalPrice !== null &&
            item.totalPrice !== undefined &&
            !isNaN(item.totalPrice);
          const hasValidTotalCost =
            item.totalCost !== null &&
            item.totalCost !== undefined &&
            !isNaN(item.totalCost);
          return hasValidTotalPrice || hasValidTotalCost;
        });

      const hasChargeToClientItems = filtered.some(
        (item) => item.chargeToClient,
      );
      const shouldShowPrice = isDelegated && hasChargeToClientItems;

      const totalCost = filtered.reduce(
        (sum, item) => sum + (item.totalCost || 0),
        0,
      );
      const totalSales = filtered.reduce(
        (sum, item) => sum + (item.totalPrice || 0),
        0,
      );

      return {
        filteredInventory: filtered,
        showPriceColumns: shouldShowPrice,
        totalCostToDate: totalCost,
        totalSalesToDate: totalSales,
        rawProfit: totalSales - totalCost,
      };
    }, [inventory, isDelegated, filterMode]);

  const tableColumns = useMemo(() => {
    const cols: any[] = [];
    if (showPriceColumns) {
      cols.push(
        {
          key: 'totalPrice',
          label: 'Total Price',
          className: 'col-span-1',
          render: (v: number) => formatCurrency(v),
        },
        {
          key: 'unitPrice',
          label: 'Unit Price',
          className: 'col-span-1',
          render: (v: number) => formatCurrency(v),
        },
      );
    }
    cols.push(
      {
        key: 'totalCost',
        label: 'Total Cost',
        className: 'col-span-1',
        render: (v: number) => formatCurrency(v),
      },
      {
        key: 'unitCost',
        label: 'Unit Cost',
        className: 'col-span-1',
        render: (v: number) => formatCurrency(v),
      },
      {
        key: 'costPerHour',
        label: 'Cost Per Hour',
        className: 'col-span-1',
        render: (v: number) => formatCurrency(v),
      },
      {
        key: 'amount',
        label: 'Amount',
        className: 'col-span-1',
        render: (v: number) => v ?? '-',
      },
      {
        key: 'name',
        label: 'Item',
        className: 'col-span-1 whitespace-nowrap',
        render: (v: string) => v || '-',
      },
      {
        key: 'type',
        label: 'Type',
        className: 'col-span-1',
        render: (_: any, row: InventoryItem) =>
          row.equipmentId ? 'Equipment' : 'Material',
      },
      {
        key: 'time',
        label: 'Date',
        className: 'col-span-1',
        render: (v: string) => formatDate(v),
      },
      {
        key: 'taskName',
        label: 'Task',
        className: 'col-span-1 whitespace-nowrap',
        render: (v: string) => v || '-',
      },
      {
        key: 'isSubcontracted',
        label: 'Subcontracted',
        className: 'col-span-1',
        render: (_: any, row: InventoryItem) => {
          if (row.unassigned && row.isOwnItem)
            return 'Previously Contracted by';
          if (row.unassigned) return 'Previous contractor';
          return row.isOwnItem ? 'No' : 'Yes';
        },
      },
      {
        key: 'organization',
        label: 'Organization',
        className: 'col-span-1',
        render: (_: any, row: InventoryItem) => {
          if (!row.isOwnItem && row.originalOrganizationName !== 'Unknown') {
            return row.originalOrganizationName || '-';
          }
          if (row.unassigned && row.isOwnItem && row.displayOrganizationName) {
            return row.displayOrganizationName;
          }
          return '-';
        },
      },
      {
        key: 'source',
        label: 'Source',
        className: 'col-span-1',
        render: (_: any, row: InventoryItem) => {
          if (!row.source) return '-';
          return (
            <SourceBadge
              source={row.source as MaterialSource}
              size="sm"
            />
          );
        },
      },
    );
    return cols;
  }, [showPriceColumns]);

  return (
    <div>
      <div className="flex items-center justify-end mb-1">
        <div className="w-56">
          <ReactSelect
            options={filterOptions}
            value={selectedFilter}
            isClearable={false}
            onChange={(value) => {
              const val = (value as SelectOption)?.value as any;
              setFilterMode(val);
            }}
          />
        </div>
      </div>

      <DataTable
        height={500}
        loading={false}
        columns={tableColumns}
        data={filteredInventory}
        compact
      />

      {(showPriceColumns || true) && (
        <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-md mt-2 grid grid-cols-2 md:grid-cols-4 gap-3">
          {showPriceColumns && (
            <div className="flex flex-col gap-0.5">
              <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                Total Sales to Date
              </p>
              <p className="text-sm font-bold text-gray-900">
                {formatCurrency(totalSalesToDate)}
              </p>
            </div>
          )}
          <div className="flex flex-col gap-0.5">
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
              Total Cost to Date
            </p>
            <p className="text-sm font-bold text-gray-900">
              {formatCurrency(totalCostToDate)}
            </p>
          </div>
          {showPriceColumns && (
            <div className="flex flex-col gap-0.5">
              <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                Raw Profit
              </p>
              <p
                className={`text-sm font-bold ${
                  rawProfit >= 0 ? 'text-green-700' : 'text-red-600'
                }`}>
                {formatCurrency(rawProfit)}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
